import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Seo title={title} noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
          <span className="font-heading font-black text-lg">
            Nox<span className="text-primary"> AI</span>
          </span>
        </Link>
        <h1 className="font-heading text-3xl font-black tracking-tight text-center">{title}</h1>
        <p className="text-muted-foreground mt-2 mb-8 text-center">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your inbox" subtitle="Password reset instructions are on their way.">
        <div className="rounded-2xl border border-white/10 bg-[#0F1015] p-8 text-center" data-testid="forgot-sent">
          <div className="h-14 w-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
            <MailCheck className="h-7 w-7 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">
            If an account exists for <span className="text-white">{email}</span>, you'll receive an email
            with a link to reset your password. The link expires in 1 hour.
          </p>
          <Link to="/login">
            <Button className="w-full mt-6 bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11" data-testid="forgot-back-login">
              Back to sign in
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Forgot password?" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="forgot-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            className="bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-11"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="forgot-submit-button"
          className="w-full bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11"
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Send reset link
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground text-center">
        Remembered it?{" "}
        <Link to="/login" className="text-primary hover:underline font-medium" data-testid="forgot-signin-link">
          Sign in
        </Link>
      </p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset! Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Shell title="Invalid link" subtitle="This reset link is missing or malformed.">
        <Link to="/forgot-password">
          <Button className="w-full bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11" data-testid="reset-request-new">
            Request a new link
          </Button>
        </Link>
      </Shell>
    );
  }

  return (
    <Shell title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            type="password"
            data-testid="reset-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            minLength={6}
            className="bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-11"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            data-testid="reset-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            required
            minLength={6}
            className="bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-11"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="reset-submit-button"
          className="w-full bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
          Reset password
        </Button>
      </form>
    </Shell>
  );
}
